import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Loading,
  Button,
  SummaryCard,
  ClinicFilterBar,
  usePersistedState,
  showSuccess,
  showApiError,
  Tooltip,
  SegmentedControl,
} from "@common";
import { formatMoney, canManageDailyBankBalance } from "@utils";
import { useAuth } from "@api/useAuth";
import useClinicFilterOptions from "@hooks/useClinicFilterOptions";
import {
  useDailyBankBalances,
  useCreateDailyBankBalance,
  useBulkCreateDailyBankBalance,
  useUpdateDailyBankBalance,
  useBankAccounts,
  useNetsuiteBankingStatements,
} from "@api/hooks";
import CurrencyBadge from "./CurrencyBadge";

// ─── helpers ──────────────────────────────────────────────────────────────────

function todayISO() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

// formatMoney (from utils/format.js) treats null/undefined as 0 — wrap it so
// a missing prior-close/change value still renders as "—" instead of "$0.00".
function fmtMoneyOrDash(v) {
  return v == null || Number.isNaN(v) ? "—" : formatMoney(v);
}

// Whole-dollar, no cents — used by the "Change vs Prior Close" summary card
// (table cells keep formatMoney's cents for precision).
function fmtWhole(v) {
  if (v == null || Number.isNaN(v)) return "—";
  const n = Math.round(v);
  return `${n < 0 ? "-" : ""}$${Math.abs(n).toLocaleString("en-CA")}`;
}

// Sign-based text color for money cells: green for positive, red for negative.
// Zero / missing values keep the caller's neutral color (`fallback`), so a "—"
// or $0 doesn't get a misleading tint.
function moneyColorClass(v, fallback = "text-palette-gray-heading") {
  if (v == null || Number.isNaN(v) || v === 0) return fallback;
  return v < 0 ? "text-smiles-redOrange" : "text-smiles-green";
}

// Live thousands-grouping for the money entry fields (e.g. "1000000" → "1,000,000").
// Keeps a leading "-", a single decimal point, and up to 2 decimals so editing
// feels natural mid-typing. Idempotent — re-running on already-grouped text is a
// no-op. Strip the commas with `unformatNumericInput` before Number()-ing.
function formatNumericInput(raw) {
  if (raw == null) return "";
  const negative = String(raw).trim().startsWith("-");
  const s = String(raw).replace(/[^\d.]/g, "");
  const dot = s.indexOf(".");
  let intPart = dot === -1 ? s : s.slice(0, dot);
  const decPart =
    dot === -1
      ? null
      : s
          .slice(dot + 1)
          .replace(/\./g, "")
          .slice(0, 2);
  intPart = intPart.replace(/^0+(?=\d)/, ""); // drop leading zeros
  const grouped = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  let out = grouped;
  if (decPart !== null) out = `${grouped === "" ? "0" : grouped}.${decPart}`;
  if (out === "") return negative ? "-" : "";
  return (negative ? "-" : "") + out;
}

// Grouped string → a value Number() can parse ("1,000,000.50" → "1000000.50").
function unformatNumericInput(s) {
  return String(s ?? "").replace(/,/g, "");
}

// The reference-only closing fields (`closing_balance`, `closing_loc_balance`)
// are JSON blobs — {balance, time} — not numbers. Anything that isn't a
// well-formed blob reads as empty rather than NaN: the column is schemaless and
// may hold older or hand-edited shapes.
function readClosingBlob(entry, field) {
  const data = entry && typeof entry[field] === "object" ? entry[field] : null;
  const raw = data?.balance;
  const amount = raw != null && raw !== "" && !Number.isNaN(Number(raw)) ? Number(raw) : null;
  return { amount, time: data?.time ?? null };
}

// Wrap an entered figure for storage. `balance` goes as a string so the decimal
// survives the round-trip without float drift, stamped with the moment it was
// captured. An empty input clears the blob outright rather than storing a null
// amount inside it.
function makeClosingBlob(amount) {
  return amount == null ? null : { balance: String(amount), time: new Date().toISOString() };
}

// NetSuite returns `asof` in whichever shape SuiteQL hands back — ISO or
// M/D/YYYY. Normalise to ISO for comparison; null means "unrecognised", and the
// caller then trusts the server-side asof filter rather than dropping the row.
function toISODate(value) {
  if (!value) return null;
  const raw = String(value).trim();
  const iso = raw.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
  if (iso) return `${iso[1]}-${iso[2].padStart(2, "0")}-${iso[3].padStart(2, "0")}`;
  const us = raw.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/);
  if (us) return `${us[3]}-${us[1].padStart(2, "0")}-${us[2].padStart(2, "0")}`;
  return null;
}

// Currencies an ACCOUNT can be denominated in. Mirrors `Currency.choices` on
// backend/apps/bank_accounts/models.py — keep in sync. Not selectable per entry
// any more: currency is account configuration, shown read-only in each row.
const CURRENCY_OPTIONS = ["CAD", "USD"];

function fmtDateShort(dateStr) {
  if (!dateStr) return "";
  return new Date(`${dateStr}T00:00:00`).toLocaleDateString("en-CA", {
    month: "short",
    day: "numeric",
  });
}

function fmtTime(dateTimeStr) {
  if (!dateTimeStr) return "";
  return new Date(dateTimeStr).toLocaleTimeString("en-CA", {
    hour: "numeric",
    minute: "2-digit",
  });
}

// Build one row per active BANK ACCOUNT, combining the most recent prior entry
// (prior close — ideally yesterday's) with the selected date's entry, if any.
// `accounts` (from /api/bank-accounts/) is the source of truth for *which* rows
// exist; `daily_bank_balance` only supplies the balance history for accounts
// already in that list.
//
// A clinic with two accounts gets two rows, and an entity-level account with no
// clinic (Dens N Dente's RBC CAD/USD pair) gets one too.
function buildRows(accounts, entriesByAccountId, selectedDate, netsuiteBalanceByAccountId) {
  return accounts
    .map((account) => {
      const accountEntries = entriesByAccountId.get(account.id) ?? [];
      const sortedDesc = [...accountEntries].sort((a, b) => (a.date < b.date ? 1 : -1));
      const current = accountEntries.find((e) => e.date === selectedDate) ?? null;
      const prior = sortedDesc.find((e) => e.date < selectedDate) ?? null;

      const priorClose = prior ? Number(prior.balance) : null;
      const currentBalance = current ? Number(current.balance) : null;
      const change = current && priorClose != null ? currentBalance - priorClose : null;

      return {
        key: String(account.id),
        accountId: account.id,
        // Owner label for the first column: clinic code when the account belongs
        // to a clinic, entity name otherwise (Dens N Dente's RBC accounts).
        owner: account.clinic_code || account.entity_name || "—",
        // Only clinic-owned accounts have a clinic profile to link to.
        clinicCode: account.clinic_code || null,
        label: account.label || "",
        // `bank_accounts.bank_type` (RBC, MCU, …) — decides the bank group.
        bankType: account.bank_type || null,
        // Needed by the Available calculation: for a credit card
        // or LOC the entered balance is money OWED, so available = limit − owed.
        accountType: account.account_type,
        creditLimit: account.credit_limit != null ? Number(account.credit_limit) : null,
        // Currency is account configuration, not a per-entry choice.
        currency: account.currency,
        priorClose,
        priorDate: prior?.date ?? null,
        current,
        change,
        locLimit: account.loc_limit != null ? Number(account.loc_limit) : null,
        // NetSuite's statement balance for the SELECTED date, or null when that
        // account has no statement filed for that exact day. Never a nearby
        // day's figure — see the query, which is a single-day window.
        netsuiteBalance:
          account.netsuite_account_id != null
            ? (netsuiteBalanceByAccountId?.get(Number(account.netsuite_account_id)) ?? null)
            : null,
      };
    })
    .sort((a, b) => {
      const byBank = bankRank(a.bankType) - bankRank(b.bankType);
      if (byBank !== 0) return byBank;
      if (a.owner === "SFC" && b.owner !== "SFC") return -1;
      if (b.owner === "SFC" && a.owner !== "SFC") return 1;
      const byOwner = a.owner.localeCompare(b.owner);
      return byOwner !== 0 ? byOwner : a.label.localeCompare(b.label);
    });
}

// Row order AND subtotal grouping by bank: RBC, MCU, BMO, DES, then TD and BNS
// together as "Other", then anything left over. Read off the account's
// `bank_type` column, which is set per account in the bank_accounts admin.
//
// Anything matching none of them (no bank_type, or one not listed here such as
// CIBC / WFCU / AMEX) sorts last and gets NO subtotal row.
const BANK_GROUPS = [
  { label: "RBC", types: ["RBC"] },
  { label: "MCU", types: ["MCU"] },
  { label: "BMO", types: ["BMO"] },
  { label: "DES", types: ["DES"] },
  // TD and BNS share one subtotal — a handful of accounts each, and the page
  // wants them read as a single line rather than two near-empty ones.
  { label: "Other", types: ["TD", "BNS"] },
];
function bankRank(bankType) {
  const i = BANK_GROUPS.findIndex((group) => group.types.includes(bankType));
  return i === -1 ? BANK_GROUPS.length : i;
}

// A credit card or LOC: the entered number is money OWED against a limit, not
// cash held.
function isLiability(row) {
  return row.accountType === "credit_card" || row.accountType === "line_of_credit";
}

// The limit shown in the LOC Limit column: a bank account's credit line, or a
// liability account's own limit (Dens N Dente's Operating LOC sits in the Bank
// view and has no `loc_limit`, only a `credit_limit`).
function lineLimit(row) {
  return isLiability(row) ? row.creditLimit : row.locLimit;
}

// Available for one row, given the figures to compute from (opening or closing):
//   liability — limit − owed.
//   bank, card view — just the cash held; there is no credit line column.
//   bank, bank view — cash + (LOC limit − LOC balance).
function availableFor(row, balance, loc, cardView) {
  if (balance == null || Number.isNaN(balance)) return null;
  if (isLiability(row)) return (row.creditLimit ?? 0) - balance;
  if (cardView) return balance;
  return balance + ((row.locLimit ?? 0) - (loc ?? 0));
}

// The four entered figures of a row, as numbers (null = blank).
//   savedFigures — what is stored for the selected date.
//   shownFigures — what the row's inputs DISPLAY, with the same precedence as
//     BalanceRow: the user's draft, then the saved value, then (balance only)
//     NetSuite's prefill. Totals built from these add up to the column above.
function savedFigures(row) {
  const num = (v) => (v == null ? null : Number(v));
  return {
    balance: row.current ? Number(row.current.balance) : null,
    loc: num(row.current?.loc_balance),
    closing: readClosingBlob(row.current, "closing_balance").amount,
    closingLoc: readClosingBlob(row.current, "closing_loc_balance").amount,
  };
}

function shownFigures(row, drafts) {
  const saved = savedFigures(row);
  const pick = (draft, fallback) => {
    const raw = unformatNumericInput(
      formatNumericInput(draft ?? (fallback != null ? String(fallback) : "")),
    );
    const n = raw === "" ? null : Number(raw);
    return n == null || Number.isNaN(n) ? null : n;
  };
  return {
    balance: pick(drafts.balance[row.key], saved.balance ?? row.netsuiteBalance),
    loc: pick(drafts.loc[row.key], saved.loc),
    closing: pick(drafts.closing[row.key], saved.closing),
    closingLoc: pick(drafts.closingLoc[row.key], saved.closingLoc),
  };
}

// Totals line for a set of rows — the footer and each bank's subtotal share it.
// `figuresOf` picks which numbers are summed (saved, or as shown). Every sum
// reports null when no row has a figure; TotalsRow still prints that as $0.00
// so every total column carries a number. Available and Closing
// Available mirror BalanceRow's per-row formulas.
function computeTotals(rows, cardView, figuresOf = savedFigures) {
  const sum = (valueOf) => {
    let total = 0;
    let any = false;
    rows.forEach((r) => {
      const v = valueOf(r, figuresOf(r));
      if (v != null) {
        total += v;
        any = true;
      }
    });
    return any ? total : null;
  };
  return {
    priorClose: sum((r) => r.priorClose),
    balance: sum((r, f) => f.balance),
    change: sum((r) => r.change),
    locBalance: sum((r, f) => f.loc),
    locLimit: sum((r) => lineLimit(r)),
    // Card view only — the Credit Limit column's total.
    creditLimit: sum((r) => r.creditLimit),
    available: sum((r, f) => availableFor(r, f.balance, f.loc, cardView)),
    closingBalance: sum((r, f) => f.closing),
    closingLocBalance: sum((r, f) => f.closingLoc),
    closingAvailable: cardView
      ? null
      : sum((r, f) => availableFor(r, f.closing, f.closingLoc, cardView)),
  };
}

// One totals line: the footer's grand total, or a bank's subtotal row.
function TotalsRow({ label, totals, currencies, cardView, className }) {
  return (
    <tr className={className}>
      <td className="px-4 py-3 text-palette-gray-primary">{label}</td>
      {/* One badge per currency actually entered, so a CAD+USD mix is
        obvious at a glance — the totals beside it are raw sums and
        only meaningful within a single currency. */}
      <td className="px-4 py-3 text-center text-xs text-palette-gray-secondary">
        {currencies.length > 0 ? (
          <span className="inline-flex flex-wrap items-center justify-center gap-1">
            {currencies.map((c) => (
              <CurrencyBadge key={c} currency={c} />
            ))}
          </span>
        ) : (
          "—"
        )}
      </td>
      <td className={`px-4 py-3 text-right tabular-nums ${moneyColorClass(totals.priorClose)}`}>
        {formatMoney(totals.priorClose)}
      </td>
      <td
        className={`px-4 py-3 text-right tabular-nums ${moneyColorClass(totals.balance, "text-palette-gray-primary")}`}
      >
        {formatMoney(totals.balance)}
      </td>
      {/* Reference totals are muted, matching their columns — they
        sum entered figures, but nothing downstream reads them. */}
      <td className="px-4 py-3 text-right tabular-nums text-palette-gray-placeholder">
        {formatMoney(totals.closingBalance)}
      </td>
      {cardView ? (
        <td className="px-4 py-3 text-right tabular-nums text-palette-gray-heading">
          {formatMoney(totals.creditLimit)}
        </td>
      ) : (
        <>
          <td className="px-4 py-3 text-right tabular-nums text-palette-gray-primary">
            {formatMoney(totals.locBalance)}
          </td>
          <td className="px-4 py-3 text-right tabular-nums text-palette-gray-placeholder">
            {formatMoney(totals.closingLocBalance)}
          </td>
          <td className="px-4 py-3 text-right tabular-nums text-palette-gray-heading">
            {formatMoney(totals.locLimit)}
          </td>
        </>
      )}
      <td className={`px-4 py-3 text-right tabular-nums ${moneyColorClass(totals.available)}`}>
        {formatMoney(totals.available)}
      </td>
      {!cardView && (
        <td className="px-4 py-3 text-right tabular-nums text-palette-gray-placeholder">
          {formatMoney(totals.closingAvailable)}
        </td>
      )}
      <td colSpan={3} />
    </tr>
  );
}

// ─── available cell (value + hover tooltip explaining the calc) ─────────────────

function AvailableCell({
  available,
  openingBalance,
  locLimit,
  locBalance,
  title = "Available Calculation",
  balanceLabel = "Opening Balance",
  locLabel = "− LOC Balance",
}) {
  const hasValue = available != null && !Number.isNaN(available);

  return (
    <Tooltip
      disabled={!hasValue}
      contentClassName="min-w-[220px]"
      triggerClassName={
        hasValue
          ? "cursor-help underline decoration-dotted decoration-palette-gray-placeholder underline-offset-2"
          : ""
      }
      content={
        <>
          <span className="block font-semibold text-sm mb-2 text-palette-indigo-hover">
            {title}
          </span>
          <div className="flex justify-between gap-6 leading-relaxed">
            <span>{balanceLabel}</span>
            <span className="font-medium">{fmtMoneyOrDash(openingBalance)}</span>
          </div>
          <div className="flex justify-between gap-6 leading-relaxed">
            <span>+ LOC Limit</span>
            <span className="font-medium">{fmtMoneyOrDash(locLimit)}</span>
          </div>
          <div className="flex justify-between gap-6 leading-relaxed">
            <span>{locLabel}</span>
            <span className="font-medium">{fmtMoneyOrDash(locBalance)}</span>
          </div>
          <div className="flex justify-between gap-6 border-t border-palette-gray-badge mt-1.5 pt-1.5">
            <span>Available</span>
            <span className="font-bold">{fmtMoneyOrDash(available)}</span>
          </div>
        </>
      }
    >
      {fmtMoneyOrDash(available)}
    </Tooltip>
  );
}

// ─── row ──────────────────────────────────────────────────────────────────────

function BalanceRow({
  row,
  selectedDate,
  draftValue,
  onDraftChange,
  draftLocValue,
  onLocDraftChange,
  draftClosingValue,
  onClosingDraftChange,
  draftClosingLocValue,
  onClosingLocDraftChange,
  editing,
  onStartEdit,
  onSubmit,
  onClinicClick,
  saving,
  canEdit,
  // "This row set is liability-shaped" — a credit card or LOC, where the entered
  // number is money OWED against a limit. True on the Visa tab of either page.
  // NOT the same question as "is this entity mode", which decides data scoping
  // and the filter bar.
  cardView = false,
}) {
  const isEntered = !!row.current;
  const isEditable = canEdit && (!isEntered || editing);
  // Precedence: what the user is typing, then what is saved, then NetSuite's
  // figure for the day. The NetSuite value is only ever a starting point — it
  // fills an empty cell and stays editable.
  const value = formatNumericInput(
    draftValue ??
      (row.current
        ? String(row.current.balance)
        : row.netsuiteBalance != null
          ? String(row.netsuiteBalance)
          : ""),
  );
  const parsedRaw = unformatNumericInput(value);
  const parsed = parsedRaw === "" ? null : Number(parsedRaw);
  const unchanged = isEntered && parsed === Number(row.current.balance);

  // Say where the number came from. Two cases both mean "this is NetSuite's
  // figure": an untouched pre-filled cell, and a saved row whose balance still
  // equals what NetSuite reports for the day. Once someone edits it to
  // something else the tag drops away, so it never vouches for a hand-typed
  // number.
  const fromNetsuite =
    row.netsuiteBalance != null &&
    (isEntered ? Number(row.current.balance) === row.netsuiteBalance : draftValue == null);

  // LOC balance: entered by the user, same as balance but optional.
  const currentLoc =
    row.current && row.current.loc_balance != null ? Number(row.current.loc_balance) : null;
  const locValue = formatNumericInput(
    draftLocValue ?? (currentLoc != null ? String(currentLoc) : ""),
  );
  const parsedLocRaw = unformatNumericInput(locValue);
  const parsedLoc = parsedLocRaw === "" ? null : Number(parsedLocRaw);
  const locUnchanged = isEntered && parsedLoc === currentLoc;

  // Closing balance and closing LOC balance: reference-only, each stored as a
  // JSON blob rather than a number, so unwrap before display. Both optional — a
  // row can still be submitted with only the opening balance filled in.
  const { amount: currentClosing, time: closingTime } = readClosingBlob(
    row.current,
    "closing_balance",
  );
  const closingValue = formatNumericInput(
    draftClosingValue ?? (currentClosing != null ? String(currentClosing) : ""),
  );
  const parsedClosingRaw = unformatNumericInput(closingValue);
  const parsedClosing = parsedClosingRaw === "" ? null : Number(parsedClosingRaw);
  const closingUnchanged = isEntered && parsedClosing === currentClosing;

  const { amount: currentClosingLoc, time: closingLocTime } = readClosingBlob(
    row.current,
    "closing_loc_balance",
  );
  const closingLocValue = formatNumericInput(
    draftClosingLocValue ?? (currentClosingLoc != null ? String(currentClosingLoc) : ""),
  );
  const parsedClosingLocRaw = unformatNumericInput(closingLocValue);
  const parsedClosingLoc = parsedClosingLocRaw === "" ? null : Number(parsedClosingLocRaw);
  const closingLocUnchanged = isEntered && parsedClosingLoc === currentClosingLoc;

  const balanceValid = parsed != null && !Number.isNaN(parsed);
  const locValid = parsedLoc == null || !Number.isNaN(parsedLoc);
  const closingValid = parsedClosing == null || !Number.isNaN(parsedClosing);
  const closingLocValid = parsedClosingLoc == null || !Number.isNaN(parsedClosingLoc);
  const canSubmit =
    balanceValid &&
    locValid &&
    closingValid &&
    closingLocValid &&
    (!isEntered || !unchanged || !locUnchanged || !closingUnchanged || !closingLocUnchanged);

  // Available differs by account, because the entered number means different
  // things — see `availableFor`. For a bank account `balance` is cash HELD:
  // available = balance + (LOC limit − LOC balance). For a credit card / LOC it
  // is the balance OWED: available credit = limit − owed. Reflects the live
  // draft so the figure updates as the user types.
  const isLiabilityAccount = isLiability(row);
  const availableBalance = balanceValid ? availableFor(row, parsed, parsedLoc, cardView) : null;

  // Closing Available — the same formula as Available, run on the two closing
  // figures instead of the opening ones. Reference only, like the closing
  // figures it is built from: nothing reads it but this cell. Null unless a
  // closing balance has been entered — with no closing figure there is nothing
  // to compute from, and showing the bare LOC headroom would look like a real
  // available position. A blank closing LOC counts as zero drawn, mirroring how
  // Available treats a blank LOC balance. Bank view only.
  const closingAvailable = cardView
    ? null
    : availableFor(row, parsedClosing, parsedClosingLoc, cardView);

  return (
    <tr className="border-b border-palette-gray-divider last:border-0">
      <td className="px-4 py-3">
        {row.clinicCode ? (
          <button
            type="button"
            onClick={() => onClinicClick(row.clinicCode)}
            title="View clinic profile"
            className="font-semibold text-palette-indigo-badge hover:text-palette-indigo-heading hover:underline"
          >
            {row.owner}
          </button>
        ) : (
          // Entity-level accounts have no clinic profile to open.
          <span className="font-semibold text-palette-gray-primary">{row.owner}</span>
        )}
        <div className="text-xs text-palette-gray-secondary truncate max-w-xs">{row.label}</div>
      </td>
      {/* Currency is a property of the account, not of the day's entry — read-only.
          Change it on the account itself (Django admin / bank accounts). */}
      <td className="px-4 py-3 text-center">
        <CurrencyBadge currency={row.currency} />
      </td>
      <td className="px-4 py-3 text-right whitespace-nowrap">
        <div className={`font-medium ${moneyColorClass(row.priorClose)}`}>
          {fmtMoneyOrDash(row.priorClose)}
        </div>
        {row.priorDate && (
          <div className="text-xs text-palette-gray-placeholder">
            close of {fmtDateShort(row.priorDate)}
          </div>
        )}
      </td>
      <td className="px-4 py-3 text-right">
        {isEditable ? (
          <input
            type="text"
            inputMode="decimal"
            value={value}
            onChange={(e) => onDraftChange(row.key, formatNumericInput(e.target.value))}
            placeholder="0.00"
            className={`ml-auto w-36 rounded-lg border border-palette-gray-border bg-white px-3 py-1.5 text-right text-sm font-medium tabular-nums focus:outline-none focus:ring-2 focus:ring-palette-indigo-label ${moneyColorClass(parsed, "text-palette-gray-primary")}`}
          />
        ) : (
          <div
            className={`ml-auto w-36 rounded-lg border border-transparent bg-palette-gray-subtle px-3 py-1.5 text-right text-sm font-medium tabular-nums ${moneyColorClass(
              row.current ? Number(row.current.balance) : null,
              "text-palette-gray-primary",
            )}`}
          >
            {fmtMoneyOrDash(row.current?.balance)}
          </div>
        )}
        {fromNetsuite && (
          <div
            className="mt-0.5 text-xs text-palette-indigo-badge"
            title={`NetSuite banking statement as of ${selectedDate}. Edit the figure to override it.`}
          >
            from NetSuite
          </div>
        )}
      </td>
      {/* Closing Balance — entered, optional, both modes. Reference only: it
          feeds no total and no calculation. The capture time is stamped on save
          and shown beneath the figure. */}
      <td className="px-4 py-3 text-right">
        {isEditable ? (
          <input
            type="text"
            inputMode="decimal"
            value={closingValue}
            onChange={(e) => onClosingDraftChange(row.key, formatNumericInput(e.target.value))}
            placeholder="—"
            className={`ml-auto w-36 rounded-lg border border-palette-gray-border bg-white px-3 py-1.5 text-right text-sm font-medium tabular-nums focus:outline-none focus:ring-2 focus:ring-palette-indigo-label ${moneyColorClass(parsedClosing, "text-palette-gray-primary")}`}
          />
        ) : (
          <div
            className={`ml-auto w-36 rounded-lg border border-transparent bg-palette-gray-subtle px-3 py-1.5 text-right text-sm font-medium tabular-nums ${moneyColorClass(
              currentClosing,
              "text-palette-gray-primary",
            )}`}
          >
            {fmtMoneyOrDash(currentClosing)}
          </div>
        )}
        {/* Shown whenever an amount is on file, including mid-edit — the stamp
            describes the SAVED figure, so it stays visible while the user types
            a replacement. Saving re-stamps it. */}
        {currentClosing != null && closingTime && (
          <div className="mt-0.5 text-xs text-palette-gray-placeholder">
            as of {fmtTime(closingTime)}
          </div>
        )}
      </td>
      {/* Clinic mode: LOC Balance is ENTERED here and LOC Limit is read from the
          clinic profile; both feed Available and ClinicCashFlow's projection. The Visa
          view drops them for a read-only Credit Limit instead. */}
      {cardView ? (
        <td className="px-4 py-3 text-right whitespace-nowrap font-medium text-palette-gray-heading">
          {fmtMoneyOrDash(row.creditLimit)}
        </td>
      ) : (
        <>
          <td className="px-4 py-3 text-right">
            {isEditable ? (
              <input
                type="text"
                inputMode="decimal"
                value={locValue}
                onChange={(e) => onLocDraftChange(row.key, formatNumericInput(e.target.value))}
                placeholder="—"
                className="ml-auto w-36 rounded-lg border border-palette-gray-border bg-white px-3 py-1.5 text-right text-sm font-medium tabular-nums text-palette-gray-primary focus:outline-none focus:ring-2 focus:ring-palette-indigo-label"
              />
            ) : (
              <div className="ml-auto w-36 rounded-lg border border-transparent bg-palette-gray-subtle px-3 py-1.5 text-right text-sm font-medium tabular-nums text-palette-gray-primary">
                {fmtMoneyOrDash(currentLoc)}
              </div>
            )}
          </td>
          {/* Closing LOC Balance — entered, optional, reference only, mirroring
              the Closing Balance column. Bank view only, because the Visa view
              has no LOC column for it to sit beside. */}
          <td className="px-4 py-3 text-right">
            {isEditable ? (
              <input
                type="text"
                inputMode="decimal"
                value={closingLocValue}
                onChange={(e) =>
                  onClosingLocDraftChange(row.key, formatNumericInput(e.target.value))
                }
                placeholder="—"
                className={`ml-auto w-36 rounded-lg border border-palette-gray-border bg-white px-3 py-1.5 text-right text-sm font-medium tabular-nums focus:outline-none focus:ring-2 focus:ring-palette-indigo-label ${moneyColorClass(parsedClosingLoc, "text-palette-gray-primary")}`}
              />
            ) : (
              <div
                className={`ml-auto w-36 rounded-lg border border-transparent bg-palette-gray-subtle px-3 py-1.5 text-right text-sm font-medium tabular-nums ${moneyColorClass(
                  currentClosingLoc,
                  "text-palette-gray-primary",
                )}`}
              >
                {fmtMoneyOrDash(currentClosingLoc)}
              </div>
            )}
            {currentClosingLoc != null && closingLocTime && (
              <div className="mt-0.5 text-xs text-palette-gray-placeholder">
                as of {fmtTime(closingLocTime)}
              </div>
            )}
          </td>
          <td className="px-4 py-3 text-right whitespace-nowrap font-medium text-palette-gray-heading">
            {fmtMoneyOrDash(lineLimit(row))}
          </td>
        </>
      )}
      <td
        className={`px-4 py-3 text-right whitespace-nowrap font-medium ${moneyColorClass(availableBalance)}`}
      >
        {/* The tooltip spells out the cash + LOC headroom formula, which a
            liability row does not use. */}
        {cardView || isLiabilityAccount ? (
          fmtMoneyOrDash(availableBalance)
        ) : (
          <AvailableCell
            available={availableBalance}
            openingBalance={parsed}
            locLimit={row.locLimit ?? 0}
            locBalance={parsedLoc ?? 0}
          />
        )}
      </td>
      {/* Closing Available — the same calculation on the closing figures.
          Reference only, so it is rendered muted rather than in the sign colours
          the real Available uses. Bank view only, matching its inputs. */}
      {!cardView && (
        <td className="px-4 py-3 text-right whitespace-nowrap text-palette-gray-placeholder">
          {isLiabilityAccount ? (
            fmtMoneyOrDash(closingAvailable)
          ) : (
            <AvailableCell
              available={closingAvailable}
              openingBalance={parsedClosing}
              locLimit={row.locLimit ?? 0}
              locBalance={parsedClosingLoc ?? 0}
              title="Closing Available Calculation"
              balanceLabel="Closing Balance"
              locLabel="− Closing LOC Balance"
            />
          )}
        </td>
      )}
      <td className="px-4 py-3">
        {isEntered ? (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-palette-emerald-soft px-2.5 py-1 text-xs font-medium text-palette-emerald-badge whitespace-nowrap">
            <span className="w-1.5 h-1.5 rounded-full bg-palette-emerald-badge" />
            {fmtTime(row.current.updated_at)}
          </span>
        ) : (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-palette-amber-soft px-2.5 py-1 text-xs font-medium text-palette-amber-badge whitespace-nowrap">
            <span className="w-1.5 h-1.5 rounded-full bg-palette-amber-badge" />
            Awaiting entry
          </span>
        )}
      </td>
      <td className="px-4 py-3 whitespace-nowrap">
        {row.current?.created_by_name ? (
          <span className="text-sm text-palette-gray-primary">{row.current.created_by_name}</span>
        ) : (
          <span className="text-palette-gray-placeholder">—</span>
        )}
      </td>
      <td className="px-4 py-3 text-right whitespace-nowrap">
        {!canEdit ? (
          <span className="text-palette-gray-placeholder">—</span>
        ) : isEntered && !editing ? (
          <Button variant="outline" size="sm" onClick={() => onStartEdit(row.key)}>
            Edit
          </Button>
        ) : (
          <Button
            variant="primary"
            size="sm"
            disabled={!canSubmit || saving}
            onClick={() =>
              onSubmit(row, parsed, parsedLoc, parsedClosing, parsedClosingLoc, selectedDate)
            }
          >
            Submit
          </Button>
        )}
      </td>
    </tr>
  );
}

// ─── main ─────────────────────────────────────────────────────────────────────

// Columns differ by view. Bank view keeps LOC Balance (an ENTERED value) and
// LOC Limit, because Available and ClinicCashFlow's projection both depend on them.
// Visa view drops both and shows the account's Credit Limit instead, with the
// entered figure relabelled — on a card it is the balance owed, not an opening
// cash balance.
function tableColsFor(cardView) {
  return [
    // A row is an account, not a practice — the cell shows the owning clinic code
    // or entity name, with the account label beneath it.
    { key: "account", label: "Account" },
    { key: "currency", label: "Currency" },
    { key: "prior", label: "Prior Close" },
    { key: "balance", label: cardView ? "Credit Balance" : "Opening Balance" },
    // Entered, optional — the end-of-day figure for the same account. Sits beside
    // the opening balance in both modes; it does not feed Available, which is an
    // opening-balance calculation.
    { key: "closing_balance", label: "Closing Balance", sublabel: "for reference" },
    ...(cardView
      ? [{ key: "credit_limit", label: "Credit Limit" }]
      : [
          { key: "loc_balance", label: "LOC Balance" },
          {
            key: "closing_loc_balance",
            label: "Closing LOC Balance",
            sublabel: "for reference",
          },
          { key: "loc_limit", label: "LOC Limit" },
        ]),
    { key: "available", label: "Available" },
    // Bank view only — it is built from the LOC figures, which the Visa view
    // does not have.
    ...(cardView
      ? []
      : [{ key: "closing_available", label: "Closing Available", sublabel: "for reference" }]),
    { key: "status", label: "Status" },
    { key: "created_by", label: "Created By" },
    { key: "action", label: "" },
  ];
}

/**
 * Daily bank balance entry — one row per active BANK ACCOUNT.
 *
 * Two modes:
 *   - default (clinic): accounts that belong to a clinic. Clinic + area-manager
 *     filters, clinic-profile links. Used by the Cash Flow page.
 *   - entity (`entityIds` set): every active account of those entities, including
 *     credit cards and lines of credit. Multi-select, like clinics. No clinic
 *     filters — an entity has no area manager and its accounts have no clinic.
 *     Used by Dens N Dente.
 *
 * Everything else — drafts, submit/edit, prior-close, Available, totals — is
 * shared, because the unit of entry is an account either way.
 */
export default function DailyBankBalance({
  canEdit: canEditProp,
  // Entity mode: the entity PKs to scope accounts to (multi-select, like clinics).
  entityIds = null,
  // Entity mode only: rendering the picker inside this component's own filter bar
  // keeps Date + Entity on one row, instead of the host page stacking a second bar.
  entityOptions = [],
  entityValue = [],
  onEntityChange = null,
  // Entity mode only: narrows to specific bank accounts. `null` means no filter,
  // so the clinic-scoped usage is untouched. Picker props are passed straight
  // through to the filter bar, same as the entity ones.
  accountIds = null,
  accountOptions = [],
  accountValue = [],
  onAccountChange = null,
}) {
  const entityMode = Array.isArray(entityIds);
  const navigate = useNavigate();
  const { user } = useAuth();
  // Mirrors the backend `CanManageDailyBankBalance` (can_manage_daily_bank_balance).
  // Every role that currently reaches /cash-flow may write here — Bookkeeper
  // included — so this is a no-op today; it exists so a view-only role added to
  // the route later (e.g. Observer, which can read but not write this resource)
  // doesn't get live inputs that 403 on submit.
  const canEdit = canEditProp ?? canManageDailyBankBalance(user);

  // Always start on today's date on every mount/refresh — daily bank balance is
  // a "today" entry workflow, so a persisted stale date (e.g. yesterday's) would
  // be misleading. Not persisted; the user can still change it within a session.
  const [selectedDate, setSelectedDate] = useState(todayISO);
  const [areaManagerFilter, setAreaManagerFilter] = usePersistedState(
    "dailyBankBalance:areaManagerFilter",
    "",
  );
  const [clinicFilters, setClinicFilters] = usePersistedState("dailyBankBalance:clinicFilters", []);
  const [awaitingOnly, setAwaitingOnly] = useState(false);
  // Debit vs Visa split. Persisted so the choice survives a tab switch, like the
  // other filters on this page.
  const [balanceMode, setBalanceMode] = usePersistedState("dailyBankBalance:balanceMode", "bank");
  const [drafts, setDrafts] = useState({});
  const [locDrafts, setLocDrafts] = useState({});
  const [closingDrafts, setClosingDrafts] = useState({});
  const [closingLocDrafts, setClosingLocDrafts] = useState({});
  const [editingKeys, setEditingKeys] = useState(() => new Set());

  // includeInactive: a clinic that's since been sold/closed can still have
  // historical daily-balance rows — don't hide them from the filter. Skipped in
  // entity mode, which has no clinic filters to populate.
  const { clinicsQuery, areaManagerOptions, clinicOptions } = useClinicFilterOptions(
    areaManagerFilter,
    { includeInactive: true, enabled: !entityMode },
  );

  // The account registry is the source of truth for which rows appear here —
  // NOT the clinic list, and not `daily_bank_balance` itself. A clinic may own
  // more than one account, so this replaces the old "one active account per
  // clinic" derivation from Clinic.netsuite_bank_balance.
  //
  // Two scopes, mutually exclusive:
  //   clinic mode (default) — `has_clinic=true`. This page is clinic-oriented
  //     (clinic + area-manager filters, clinic-profile links), so entity-level
  //     accounts are excluded server-side.
  //   entity mode (`entityIds` prop, used by the Dens N Dente page) — every
  //     active ENTITY-LEVEL account of those entities, including credit cards and
  //     LOCs. `has_clinic=false` is the exact complement of clinic mode: an account
  //     carrying both a clinic and an entity (the SFC pseudo-clinic's RBC S.F.
  //     Corporation - [9411]) shows up in clinic mode only, never on both pages.
  const accountsQuery = useBankAccounts(
    entityMode
      ? {
          status: "active",
          entity_id: (entityIds ?? []).join(","),
          has_clinic: "false",
          limit: 500,
        }
      : { status: "active", has_clinic: "true", limit: 500 },
    { enabled: !entityMode || (entityIds ?? []).length > 0 },
  );

  const accounts = useMemo(
    () => accountsQuery.data?.results ?? accountsQuery.data ?? [],
    [accountsQuery.data],
  );

  // In entity mode the server already scoped by entity, so the only extra
  // narrowing is the Accounts picker. In clinic mode every account has a clinic
  // (has_clinic=true), so both filters match on clinic code without a null branch.
  const visibleAccounts = useMemo(() => {
    if (entityMode) {
      if (!Array.isArray(accountIds)) return accounts;
      const keep = new Set(accountIds);
      return accounts.filter((a) => keep.has(a.id));
    }
    if (clinicFilters.length > 0) {
      const allowed = new Set(clinicFilters);
      return accounts.filter((a) => allowed.has(a.clinic_code));
    }
    // No clinic picked → show nothing rather than every account. An area
    // manager on its own is still a selection, so it keeps narrowing to that
    // manager's clinics.
    if (!areaManagerFilter) return [];
    const allowed = new Set(clinicOptions.map((o) => o.value));
    return accounts.filter((a) => allowed.has(a.clinic_code));
  }, [entityMode, accounts, accountIds, clinicFilters, areaManagerFilter, clinicOptions]);

  // NetSuite statement balances for the selected date. The endpoint takes a
  // single `asof` day, so only a statement filed AS OF that day can match — a
  // neighbouring day's balance must never be presented as this day's. An
  // account with no statement that day simply has no entry here.
  const netsuiteAccountIds = useMemo(
    () =>
      visibleAccounts
        .map((a) => a.netsuite_account_id)
        .filter((id) => id != null)
        .map(Number),
    [visibleAccounts],
  );
  const netsuiteStatementsQuery = useNetsuiteBankingStatements(
    { ids: netsuiteAccountIds, asof: selectedDate },
    { enabled: netsuiteAccountIds.length > 0 && !!selectedDate },
  );

  // netsuite account id → balance for the selected date. Rows come back ordered
  // by createddate, so a later statement for the same account and day overwrites
  // an earlier one: the most recently filed figure is the current one.
  const netsuiteBalanceByAccountId = useMemo(() => {
    const items = netsuiteStatementsQuery.data?.items ?? [];
    const out = new Map();
    items.forEach((item) => {
      const accountId = Number(item.account);
      const balance = Number(item.balance);
      if (Number.isNaN(accountId) || Number.isNaN(balance)) return;
      // The endpoint already filters on asof; re-check here because "the number
      // is for the selected day" is the entire guarantee being made. An asof we
      // cannot parse falls back to trusting the server filter.
      const asof = toISODate(item.asof);
      if (asof && asof !== selectedDate) return;
      out.set(accountId, balance);
    });
    return out;
  }, [netsuiteStatementsQuery.data, selectedDate]);

  const balancesQuery = useDailyBankBalances({});
  const createBalance = useCreateDailyBankBalance();
  const bulkCreateBalance = useBulkCreateDailyBankBalance();
  const updateBalance = useUpdateDailyBankBalance();

  const rows = useMemo(() => {
    const entries = balancesQuery.data?.results ?? balancesQuery.data ?? [];
    // Group by the account FK id — the row identity is the account now.
    const entriesByAccountId = new Map();
    entries.forEach((e) => {
      if (!entriesByAccountId.has(e.bank_account)) entriesByAccountId.set(e.bank_account, []);
      entriesByAccountId.get(e.bank_account).push(e);
    });
    return buildRows(visibleAccounts, entriesByAccountId, selectedDate, netsuiteBalanceByAccountId);
  }, [balancesQuery.data, visibleAccounts, selectedDate, netsuiteBalanceByAccountId]);

  // The Debit/Visa toggle is only meaningful where card accounts actually exist
  // (entity mode, today) — clinic-scoped accounts are all bank/LOC, so rather
  // than offer a segment that can only ever be empty, hide the control there.
  const hasCardAccounts = useMemo(() => rows.some((r) => r.accountType === "credit_card"), [rows]);

  // Everything the user sees below — counts, totals, currency mix, the table —
  // is scoped to the picked mode. "Visa" is the credit-card accounts; "Bank" is
  // everything else (bank and lines of credit), which is what the LOC-based
  // Available column is built for.
  const modeRows = useMemo(() => {
    if (!hasCardAccounts) return rows;
    return balanceMode === "visa"
      ? rows.filter((r) => r.accountType === "credit_card")
      : rows.filter((r) => r.accountType !== "credit_card");
  }, [rows, balanceMode, hasCardAccounts]);

  // Column set follows the ACCOUNTS ON SCREEN, not the page: both the clinic page
  // and Dens N Dente show the bank columns (Opening / LOC / Closing Available)
  // on Bank, and Credit Balance / Credit Limit on Visa. A line of credit stays
  // in Bank and is computed as limit − owed there — see `availableFor`.
  const cardView = hasCardAccounts && balanceMode === "visa";

  // Why the table is empty, in the reader's terms. The cases are genuinely
  // different: nothing picked yet (no request was made), a filter that excluded
  // everything, and no accounts on file at all. One message for all three would
  // send the reader looking for the wrong problem.
  const emptyMessage = useMemo(() => {
    if (awaitingOnly) return "All accounts entered for this date.";
    const noun = entityMode ? "entities" : "clinics";
    const nothingPicked = entityMode
      ? (entityIds ?? []).length === 0
      : clinicFilters.length === 0 && !areaManagerFilter;
    if (nothingPicked) return `Select one or more ${noun} to view balances.`;
    if (accounts.length > 0 && visibleAccounts.length === 0) {
      return "No accounts match the current Accounts filter.";
    }
    return "No bank accounts on file yet.";
  }, [
    awaitingOnly,
    entityMode,
    entityIds,
    clinicFilters,
    areaManagerFilter,
    accounts,
    visibleAccounts,
  ]);

  const visibleRows = awaitingOnly ? modeRows.filter((r) => !r.current) : modeRows;

  const enteredCount = modeRows.filter((r) => !!r.current).length;
  const awaitingCount = modeRows.length - enteredCount;
  // Saved entries only — feeds the "Total Entered Balance" summary cards.
  const totals = useMemo(() => computeTotals(modeRows, cardView), [modeRows, cardView]);

  // The table's footer and per-bank subtotals are COLUMN totals: they add the
  // rows on screen, using the figures those rows display (drafts and NetSuite
  // prefills included), so each one matches the column above it.
  const shownOf = useMemo(() => {
    const all = {
      balance: drafts,
      loc: locDrafts,
      closing: closingDrafts,
      closingLoc: closingLocDrafts,
    };
    return (r) => shownFigures(r, all);
  }, [drafts, locDrafts, closingDrafts, closingLocDrafts]);
  const columnTotals = useMemo(
    () => computeTotals(visibleRows, cardView, shownOf),
    [visibleRows, cardView, shownOf],
  );

  // Per-bank subtotals, drawn under the last row of each group. Bank accounts
  // only — the Visa view has no bank grouping.
  const subtotalsByLastRow = useMemo(() => {
    const out = new Map();
    if (cardView) return out;
    BANK_GROUPS.forEach(({ label: bank }, rank) => {
      const group = visibleRows.filter((r) => bankRank(r.bankType) === rank);
      if (!group.length) return;
      const entered = group.filter((r) => r.current);
      out.set(group.at(-1).key, {
        bank,
        totals: computeTotals(group, cardView, shownOf),
        entered: entered.length,
        currencies: [...new Set(group.map((r) => r.currency))],
      });
    });
    return out;
  }, [visibleRows, cardView, shownOf]);
  const subtotalAfter = (i) => subtotalsByLastRow.get(visibleRows[i].key);

  // Totals add raw amounts together, which is only meaningful within a single
  // currency. Surface the mix rather than silently reporting a CAD+USD sum as
  // if it were one number — there's no FX rate source in the app to convert with.
  const enteredCurrencies = useMemo(
    () => [...new Set(modeRows.filter((r) => r.current).map((r) => r.currency))],
    [modeRows],
  );
  const isMixedCurrency = enteredCurrencies.length > 1;

  // Persist the NetSuite figure for any row that has no entry yet for the
  // selected date. Deliberately narrow, because this writes to the database
  // without anyone pressing Submit:
  //   - CREATE only. An entry someone already made is never touched, so a
  //     hand-corrected balance cannot be reverted to NetSuite's.
  //   - Only when NetSuite has a statement for that exact date (`netsuiteBalance`
  //     is null otherwise), so nothing is invented for a day with no data.
  //   - Only for users who may write at all.
  // The guard set makes each (account, date) at most one attempt per mount:
  // failures are NOT retried, or a row rejected by the unique constraint (say,
  // created in another tab a second earlier) would fire on every render.
  const autoSavedRef = useRef(new Set());
  useEffect(() => {
    if (!canEdit) return;
    if (netsuiteStatementsQuery.isLoading || balancesQuery.isLoading) return;

    // Gather every fillable row first and write them in ONE request. Creating
    // them one at a time meant a POST per account plus a list refetch per POST,
    // which is what made a single page load produce dozens of round trips.
    const pending = [];
    rows.forEach((row) => {
      if (row.current || row.netsuiteBalance == null) return;
      const guard = `${row.accountId}|${selectedDate}`;
      if (autoSavedRef.current.has(guard)) return;
      autoSavedRef.current.add(guard);
      pending.push({
        bank_account: row.accountId,
        balance: row.netsuiteBalance,
        date: selectedDate,
      });
    });
    if (pending.length === 0) return;

    bulkCreateBalance.mutateAsync(pending).catch(() => {
      // Silent: this is a background convenience, not something the user asked
      // for, so a failure should not raise a toast over their work. The cells
      // still show the NetSuite figures, and Submit saves them.
    });
  }, [
    rows,
    canEdit,
    selectedDate,
    netsuiteStatementsQuery.isLoading,
    balancesQuery.isLoading,
    bulkCreateBalance,
  ]);

  function handleDraftChange(key, value) {
    setDrafts((prev) => ({ ...prev, [key]: value }));
  }

  function handleLocDraftChange(key, value) {
    setLocDrafts((prev) => ({ ...prev, [key]: value }));
  }

  function handleClosingDraftChange(key, value) {
    setClosingDrafts((prev) => ({ ...prev, [key]: value }));
  }

  function handleClosingLocDraftChange(key, value) {
    setClosingLocDrafts((prev) => ({ ...prev, [key]: value }));
  }

  function handleStartEdit(key) {
    setEditingKeys((prev) => new Set(prev).add(key));
  }

  function goToClinicProfile(code) {
    navigate(`/admin?tab=clinicProfile&profileClinic=${encodeURIComponent(code)}`);
  }

  async function handleSubmit(row, balance, locBalance, closingBalance, closingLoc, date) {
    // Both closing fields are reference-only JSON blobs, not numbers — see
    // makeClosingBlob. Each is stamped with the moment it was captured, so the
    // two can legitimately carry different times on the same row.
    const closingPayload = makeClosingBlob(closingBalance);
    const closingLocPayload = makeClosingBlob(closingLoc);
    try {
      if (row.current) {
        await updateBalance.mutateAsync({
          id: row.current.id,
          balance,
          loc_balance: locBalance,
          closing_balance: closingPayload,
          closing_loc_balance: closingLocPayload,
        });
        showSuccess("Balance updated.");
        setEditingKeys((prev) => {
          const next = new Set(prev);
          next.delete(row.key);
          return next;
        });
      } else {
        // Keyed on the account now — a clinic code would be ambiguous, since a
        // clinic can own more than one account. Currency comes from the account.
        await createBalance.mutateAsync({
          bank_account: row.accountId,
          balance,
          loc_balance: locBalance,
          closing_balance: closingPayload,
          closing_loc_balance: closingLocPayload,
          date,
        });
        showSuccess("Balance submitted.");
      }
      setDrafts((prev) => {
        const next = { ...prev };
        delete next[row.key];
        return next;
      });
      setLocDrafts((prev) => {
        const next = { ...prev };
        delete next[row.key];
        return next;
      });
      setClosingDrafts((prev) => {
        const next = { ...prev };
        delete next[row.key];
        return next;
      });
      setClosingLocDrafts((prev) => {
        const next = { ...prev };
        delete next[row.key];
        return next;
      });
    } catch (err) {
      showApiError(err);
    }
  }

  const saving = createBalance.isPending || updateBalance.isPending;
  // Everything the page needs before any figure on it can be trusted. The
  // NetSuite fetch is included: without it the Opening Balance column would
  // render blank and then visibly rewrite itself once the statements land.
  const isLoading =
    balancesQuery.isLoading ||
    clinicsQuery.isLoading ||
    accountsQuery.isLoading ||
    netsuiteStatementsQuery.isLoading;

  return (
    <div className="relative">
      {/* Blurred behind the overlay rather than replaced by a spinner: the
          layout stays put, so nothing jumps when the data lands. `inert` on the
          wrapper (plus pointer-events-none on the overlay) keeps a half-loaded
          table out of reach of clicks and the keyboard while it is unreadable. */}
      <div
        className={`transition ${isLoading ? "blur-sm pointer-events-none select-none" : ""}`}
        {...(isLoading ? { inert: "" } : {})}
      >
        {/* Summary cards */}
        <div className="flex flex-wrap gap-3 mb-4">
          <SummaryCard
            label={`Entered — ${fmtDateShort(selectedDate)}`}
            value={`${enteredCount} of ${modeRows.length}`}
            tag={awaitingCount > 0 ? `${awaitingCount} awaiting` : "All entered"}
          />
          <SummaryCard
            label="Total Entered Balance"
            value={fmtWhole(totals.balance ?? 0)}
            tag={isMixedCurrency ? `unconverted ${enteredCurrencies.join(" + ")}` : undefined}
          />
          <SummaryCard
            label="Change vs Prior Close"
            value={fmtWhole(totals.change ?? 0)}
            valueClass={moneyColorClass(totals.change, "text-palette-gray-primary")}
            tag={isMixedCurrency ? `unconverted ${enteredCurrencies.join(" + ")}` : undefined}
          />
        </div>

        {/* Filters */}
        {/* Entity mode: Date + Entity, no clinic/area-manager (an entity has
          neither). The entity picker is rendered here rather than by the host page
          so both controls sit on one row. */}
        {entityMode ? (
          <ClinicFilterBar
            dateValue={selectedDate}
            onDateChange={setSelectedDate}
            maxDate={todayISO()}
            showAreaManager={false}
            showClinic={!!onEntityChange}
            entityOptions={entityOptions}
            entityValue={entityValue}
            onEntityChange={onEntityChange ?? undefined}
            accountOptions={accountOptions}
            accountValue={accountValue}
            onAccountChange={onAccountChange ?? undefined}
          />
        ) : (
          <ClinicFilterBar
            dateValue={selectedDate}
            onDateChange={setSelectedDate}
            maxDate={todayISO()}
            areaManagerFilter={areaManagerFilter}
            areaManagerOptions={areaManagerOptions}
            onAreaManagerChange={(v) => {
              setAreaManagerFilter(v);
              setClinicFilters([]);
            }}
            clinicOptions={clinicOptions}
            clinicValue={clinicFilters}
            onClinicChange={setClinicFilters}
          />
        )}
        {hasCardAccounts && (
          <SegmentedControl
            className="mb-3 w-fit"
            options={[
              { value: "bank", label: "Bank" },
              { value: "visa", label: "Visa" },
            ]}
            value={balanceMode}
            onChange={setBalanceMode}
          />
        )}
        <div className="rounded-2xl border border-palette-gray-divider bg-white shadow-sm overflow-hidden">
          {/* Header */}
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-palette-gray-divider px-5 py-4">
            <div>
              <h3 className="text-base font-semibold text-palette-gray-primary">
                Daily Balance — {fmtDateShort(selectedDate)}
              </h3>
              <p className="text-sm text-palette-gray-secondary">
                Manual entry · {CURRENCY_OPTIONS.join(" / ")}
                {isMixedCurrency && (
                  <span className="ml-2 text-smiles-redOrange">
                    Totals below add {enteredCurrencies.join(" + ")} without conversion.
                  </span>
                )}
              </p>
            </div>
            <label className="flex items-center gap-2 text-sm text-palette-gray-heading cursor-pointer select-none">
              <span
                onClick={() => setAwaitingOnly((v) => !v)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  awaitingOnly ? "bg-palette-indigo-badge" : "bg-palette-gray-border"
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    awaitingOnly ? "translate-x-6" : "translate-x-1"
                  }`}
                />
              </span>
              Awaiting only
            </label>
          </div>

          {/* Body. No inline spinner: the page-level overlay below covers loading,
            and two spinners on one screen read as two separate waits. */}
          {isLoading ? (
            <div className="py-16" />
          ) : visibleRows.length === 0 ? (
            <div className="flex items-center justify-center py-16 text-sm text-palette-gray-placeholder">
              {/* The Debit/Visa split can empty the table on its own — say so,
                rather than blaming the filters that did match rows. */}
              {rows.length > 0 && modeRows.length === 0
                ? balanceMode === "visa"
                  ? "No Visa accounts in this selection."
                  : "No bank accounts in this selection."
                : emptyMessage}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="cf-table w-full border-collapse text-sm">
                <thead>
                  <tr className="border-b border-palette-gray-divider bg-palette-gray-subtle">
                    {tableColsFor(cardView).map((col) => (
                      <th
                        key={col.key}
                        className={`px-4 py-2 text-xs font-semibold uppercase tracking-wider text-palette-gray-secondary whitespace-nowrap ${
                          col.key === "currency"
                            ? "text-center"
                            : col.key === "account" ||
                                col.key === "status" ||
                                col.key === "created_by" ||
                                col.key === "action"
                              ? "text-left"
                              : "text-right"
                        }`}
                      >
                        {col.label}
                        {/* Lower-case, un-tracked: a note about the column, not
                          part of its name. */}
                        {col.sublabel && (
                          <div className="mt-0.5 text-[10px] font-normal normal-case tracking-normal text-palette-gray-placeholder">
                            {col.sublabel}
                          </div>
                        )}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {visibleRows.map((row, i) => (
                    <Fragment key={row.key}>
                      <BalanceRow
                        row={row}
                        selectedDate={selectedDate}
                        draftValue={drafts[row.key]}
                        onDraftChange={handleDraftChange}
                        draftLocValue={locDrafts[row.key]}
                        onLocDraftChange={handleLocDraftChange}
                        draftClosingValue={closingDrafts[row.key]}
                        onClosingDraftChange={handleClosingDraftChange}
                        draftClosingLocValue={closingLocDrafts[row.key]}
                        onClosingLocDraftChange={handleClosingLocDraftChange}
                        editing={editingKeys.has(row.key)}
                        onStartEdit={handleStartEdit}
                        onSubmit={handleSubmit}
                        onClinicClick={goToClinicProfile}
                        saving={saving}
                        canEdit={canEdit}
                        cardView={cardView}
                      />
                      {subtotalAfter(i) && (
                        <>
                          <TotalsRow
                            label={`${subtotalAfter(i).bank} subtotal · ${subtotalAfter(i).entered} entered`}
                            totals={subtotalAfter(i).totals}
                            currencies={subtotalAfter(i).currencies}
                            cardView={cardView}
                            className="border-b-2 border-palette-gray-border bg-palette-gray-subtle/60 text-sm font-semibold"
                          />
                          {/* Breathing room between bank groups. Skipped after the
                              LAST group, where the next thing on screen is the
                              grand total — a gap there would read as the footer
                              being detached from the table rather than as a
                              separator between two groups. `colSpan` comes from the
                              same column list the header is built from, so it
                              cannot drift when a column is added or when entity
                              mode swaps LOC Balance/Limit for Credit Limit. */}
                          {i < visibleRows.length - 1 && (
                            <tr aria-hidden="true">
                              <td colSpan={tableColsFor(cardView).length} className="h-4 p-0" />
                            </tr>
                          )}
                        </>
                      )}
                    </Fragment>
                  ))}
                </tbody>
                <tfoot>
                  <TotalsRow
                    label={`Total · ${enteredCount} entered account${enteredCount === 1 ? "" : "s"}`}
                    totals={columnTotals}
                    currencies={[...new Set(visibleRows.map((r) => r.currency))]}
                    cardView={cardView}
                    className="border-t border-palette-gray-divider bg-palette-gray-subtle font-semibold"
                  />
                </tfoot>
              </table>
            </div>
          )}
        </div>
      </div>
      {isLoading && (
        <div className="absolute inset-0 z-10 flex items-start justify-center pt-10 bg-white/60 backdrop-blur-sm pointer-events-none">
          <Loading text="Loading balances..." size="lg" />
        </div>
      )}
    </div>
  );
}
